import {Component, EventEmitter, Input, OnChanges, Output, ViewChild} from '@angular/core';
import {FormBuilder, UntypedFormGroup} from '@angular/forms';
import {
    AuditActivitiesFilterSpec,
    AuditActivity,
    AuditService,
    AssessmentTask,
    HydratedAuditTarget
} from '@webmate-web-core/shared/services/audit.service';
import {AuditId} from '@webmate-web-core/shared/types/ids';
import {AuditRequirement} from '@webmate-web-core/shared/tools/audit/audittemplate/shared';
import {SectionGeneratorConfig, SectionGeneratorId, StaticSectionGeneratorConfig} from '@webmate-web-core/shared/types/audit';
import {BrowserSessionStateArtifact} from '@webmate-web-core/shared/types/browsersession';
import {FindingSeverity, Spec} from '@webmate-web-core/shared/types/testmgmt';
import {ReportSectionContent, TextContent} from '../../../../../report/report_types';
import {SelectItem} from 'primeng/api';
import {Popover} from 'primeng/popover';
import {getRandomUUID} from '@webmate-web-core/shared/utils/utils';
import {
    faBan,
    faChevronRight,
    faEye,
    faFlagCheckered,
    faForward,
    faGlobe,
    faHandPaper,
    faHourglass,
    faPlus,
    faQuestionCircle,
    faRunning,
    faTasks,
    faThumbsDown,
    faThumbsUp,
    faTrash
} from '@fortawesome/pro-solid-svg-icons';

export function makeFormFromSectionGeneratorConfig(fb: FormBuilder, config: SectionGeneratorConfig): UntypedFormGroup {
    if (config.type === 'StaticSectionGeneratorConfig') {
        const subsectionForms: any = {};
        for (const subsection of config.subsections || []) {
            subsectionForms[subsection.id] = makeFormFromSectionGeneratorConfig(fb, subsection);
        }
        return fb.group({
            type: ['StaticSectionGeneratorConfig'],
            title: [config.title],
            content: [config.content],
            subsections: fb.group(subsectionForms),
            styleClasses: [config.styleClasses]
        });
    } else if (config.type === 'ActivitiesSectionGeneratorConfig') {
        return fb.group({
            type: ['ActivitiesSectionGeneratorConfig'],
            title: [config.title],
            activitiesQuery: [config.activitiesQuery],
            grouping: [config.grouping?.[0]],
            includedStats: [config.includedStats || []],
            includeOverviewSection: [config.includeOverviewSection ?? true],
            styleClasses: [config.styleClasses]
        });
    } else if (config.type === 'RequirementResultMatrixSectionGeneratorConfig') {
        return fb.group({
            type: ['RequirementResultMatrixSectionGeneratorConfig'],
            title: [config.title],
            activitiesQuery: [config.activitiesQuery],
            includeLegend: [config.includeLegend ?? true],
            caption: [config.caption],
            styleClasses: [config.styleClasses]
        });
    } else {
        return fb.group({
            type: ['TableOfContentsGeneratorConfig'],
            title: [config.title],
            styleClasses: [config.styleClasses]
        });
    }
}

@Component({
    selector: 'wm-report-section-generators',
    templateUrl: './report-section-generators.component.html',
    styleUrls: ['./report-section-generators.component.less']
})
export class ReportSectionGeneratorsComponent implements OnChanges {

    @Input() generatorConfigs: SectionGeneratorConfig[] = [];
    @Input() currentFormGroup: UntypedFormGroup;
    @Input() allowEdit = false;

    // Optional audit context — required for activities filter and preview
    @Input() auditId?: AuditId;
    @Input() assessmentTasks?: AssessmentTask[];
    @Input() states?: { [id: string]: BrowserSessionStateArtifact };
    @Input() testObjects?: HydratedAuditTarget[];
    @Input() auditRequirements?: AuditRequirement[];
    @Input() specsList?: Spec[];
    @Input() specs?: { [id: string]: Spec };
    @Input() statesByTestObjectId?: { [id: string]: BrowserSessionStateArtifact };
    @Input() testObjectsById?: { [id: string]: HydratedAuditTarget };
    @Input() activitiesLoading?: boolean;
    @Input() filteredActivities?: { [id: string]: AuditActivity[] };
    @Input() selectedFindingSeverities?: FindingSeverity[];

    @Output() activitiesFilterChanged = new EventEmitter<{ generatorId: string; filter: AuditActivitiesFilterSpec }>();
    @Output() generatorsChanged = new EventEmitter<SectionGeneratorConfig[]>();

    @ViewChild('filterPreviewPanel') filterPreviewPanel: Popover;

    sections: SectionGeneratorConfig[] = [];
    isGeneratorExpanded: { [id: string]: boolean } = {};
    internalFilteredActivities: { [id: string]: AuditActivity[] } = {};
    activitiesFilters: { [id: string]: AuditActivitiesFilterSpec } = {};

    readonly ActivitiesGroupingOptions: SelectItem[] = [
        {label: 'Test Object', value: 'testObject'},
        {label: 'Spec', value: 'spec'},
        {label: 'Severity', value: 'severity'}
    ];

    readonly faChevronRight = faChevronRight;
    readonly faEye = faEye;
    readonly faQuestionCircle = faQuestionCircle;
    readonly faThumbsUp = faThumbsUp;
    readonly faThumbsDown = faThumbsDown;
    readonly faBan = faBan;
    readonly faTasks = faTasks;
    readonly faRunning = faRunning;
    readonly faHourglass = faHourglass;
    readonly faFlagCheckered = faFlagCheckered;
    readonly faForward = faForward;
    readonly faHandPaper = faHandPaper;
    readonly faGlobe = faGlobe;
    readonly faPlus = faPlus;
    readonly faTrash = faTrash;

    constructor(private fb: FormBuilder, private auditService: AuditService) {}

    ngOnChanges() {
        // Sync sections from input, initializing expanded state for new entries
        this.sections = [...(this.generatorConfigs || [])];
        for (const section of this.sections) {
            this.isGeneratorExpanded[section.id] ??= false;
        }
    }

    toggleGenerator(id: string) {
        this.isGeneratorExpanded[id] = !this.isGeneratorExpanded[id];
    }

    updateActivitiesFilter(generatorId: SectionGeneratorId, filter: AuditActivitiesFilterSpec) {
        this.activitiesFilters[generatorId] = filter;
        this.activitiesFilterChanged.emit({ generatorId, filter });
    }

    showFilterPreview(event: MouseEvent, generatorId: SectionGeneratorId) {
        if (!this.auditId) return;
        const filter = this.activitiesFilters[generatorId] || {};
        this.auditService.queryAuditActivitiesOfAudit$(this.auditId, filter).subscribe(activities => {
            this.internalFilteredActivities[generatorId] = activities;
            this.filterPreviewPanel?.show(event);
        });
    }

    getTaskTargetName(task: AssessmentTask): string {
        const targetId = task.testObjectId;
        return (targetId ? this.testObjectsById?.[targetId]?.target?.name : undefined)
            || (targetId ? this.statesByTestObjectId?.[targetId]?.name : undefined)
            || 'Unknown target';
    }

    deleteSection(sectionId: string) {
        this.sections = this.sections.filter(s => s.id !== sectionId);
        this.currentFormGroup.removeControl(sectionId);
        delete this.isGeneratorExpanded[sectionId];
        this.generatorsChanged.emit(this.sections);
    }

    addStaticSection() {
        const newSection: StaticSectionGeneratorConfig = {
            type: 'StaticSectionGeneratorConfig',
            id: getRandomUUID(),
            title: 'New Section',
            content: [],
            subsections: []
        };
        this.currentFormGroup.addControl(newSection.id, makeFormFromSectionGeneratorConfig(this.fb, newSection));
        this.isGeneratorExpanded[newSection.id] = false;
        this.sections = [...this.sections, newSection];
        this.generatorsChanged.emit(this.sections);
    }

    addTextContent(sectionId: string) {
        const newItem: TextContent = { type: 'TextContent', text: '' };
        this.updateStaticSection(sectionId, s => ({ ...s, content: [...(s.content || []), newItem] }));
    }

    deleteContent(sectionId: string, idx: number) {
        this.updateStaticSection(sectionId, s => ({
            ...s, content: (s.content || []).filter((_, i) => i !== idx)
        }));
    }

    updateContent(sectionId: string, idx: number, updated: ReportSectionContent) {
        this.updateStaticSection(sectionId, s => ({
            ...s, content: (s.content || []).map((c, i) => i === idx ? updated : c)
        }));
    }

    addSubsection(parentSectionId: string) {
        const newSub: StaticSectionGeneratorConfig = {
            type: 'StaticSectionGeneratorConfig',
            id: getRandomUUID(),
            title: 'New Subsection',
            content: [],
            subsections: []
        };
        const subsectionsGroup = this.currentFormGroup
            .get(parentSectionId)?.get('subsections') as UntypedFormGroup;
        subsectionsGroup?.addControl(newSub.id, makeFormFromSectionGeneratorConfig(this.fb, newSub));
        this.isGeneratorExpanded[newSub.id] = false;
        this.updateStaticSection(parentSectionId, s => ({
            ...s, subsections: [...(s.subsections || []), newSub]
        }));
    }

    deleteSubsection(parentSectionId: string, subsectionId: string) {
        const subsectionsGroup = this.currentFormGroup
            .get(parentSectionId)?.get('subsections') as UntypedFormGroup;
        subsectionsGroup?.removeControl(subsectionId);
        delete this.isGeneratorExpanded[subsectionId];
        this.updateStaticSection(parentSectionId, s => ({
            ...s, subsections: (s.subsections || []).filter(sub => sub.id !== subsectionId)
        }));
    }

    private updateStaticSection(
        sectionId: string,
        updater: (s: StaticSectionGeneratorConfig) => StaticSectionGeneratorConfig
    ) {
        this.sections = this.sections.map(s => {
            if (s.id !== sectionId || s.type !== 'StaticSectionGeneratorConfig') return s;
            const updated = updater(s as StaticSectionGeneratorConfig);
            this.currentFormGroup.get(sectionId)?.get('content')?.setValue(updated.content);
            return updated;
        });
        this.generatorsChanged.emit(this.sections);
    }
}
